import { Modal, ModalSizeEnum, NeutralButton, PrimaryButton, toast } from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import {
  createReminderEmail,
  editReminderEmail,
  enableReminder,
} from 'modules/courses/courses/api/courses-api'
import { CourseEditApiInterface } from 'modules/courses/courses/types/course-interface'
import {
  ReminderEmailErrorResponseInterface,
  ReminderEmailInterface,
} from 'modules/courses/courses/types/reminder-interface'
import React, { FC, useEffect, useState } from 'react'
import DashboardMailEditor from 'shared/components/dashboard-mail-editor'
import { EmailErrorType } from 'shared/components/dashboard-mail-editor/types/email-interface'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { KeyedMutator } from 'swr'
import { SaveIcon } from '@systemeio/ui/icons'

interface Props {
  data?: ReminderEmailInterface
  opened: boolean
  courseId: number
  onClose: () => void
  isDataLoading: boolean
  mutateData: KeyedMutator<ReminderEmailInterface>
  mutateCourse: KeyedMutator<CourseEditApiInterface>
  hasReminderSettings: boolean
}

const defaultErrors: EmailErrorType = {
  body: '',
  fromEmail: '',
  fromName: '',
  subject: '',
  previewText: '',
  common: [],
}

const initialValues: ReminderEmailInterface = {
  currentEmail: {
    subject: '',
    body: '',
    fromName: '',
    fromEmail: '',
    previewText: '',
    useNewEditor: false,
  },
}

const ReminderEmailModal: FC<Props> = ({
  data,
  opened,
  courseId,
  onClose,
  isDataLoading,
  mutateData,
  mutateCourse,
  hasReminderSettings,
}) => {
  const { t } = useLocoTranslation()

  const [tempState, setTempState] = useState<ReminderEmailInterface>(initialValues)

  const [errors, setErrors] = useState<EmailErrorType>(defaultErrors)

  const [isFetching, setIsFetching] = useState(false)

  useEffect(() => {
    if (data) {
      setTempState({
        currentEmail: {
          subject: data?.currentEmail?.subject,
          body: data?.currentEmail?.body,
          fromName: data?.currentEmail?.fromName,
          fromEmail: data?.currentEmail?.fromEmail,
          useNewEditor: data?.currentEmail?.useNewEditor,
          previewText: data?.currentEmail?.previewText,
        },
      })
    } else {
      setTempState(initialValues)
    }
  }, [data, opened])

  const onSave = async () => {
    try {
      setErrors(defaultErrors)

      setIsFetching(true)

      const updateData = async (data: ReminderEmailInterface) => {
        await mutateCourse(currentData => {
          if (currentData) {
            return {
              ...currentData,
              hasReminderEmailSettings: true,
            }
          }
        }, false)

        await mutateData(data, false)
      }

      if (data) {
        const { data } = await editReminderEmail(courseId, tempState)

        await updateData(data)
      } else {
        const { data } = await createReminderEmail(courseId, tempState)

        await updateData(data)
      }

      if (hasReminderSettings) {
        await enableReminder(courseId)
      }

      toast.success(t('global.changes_saved'))

      onClose()
    } catch (e) {
      if (e instanceof BadRequest) {
        const errors = e.errors as unknown as ReminderEmailErrorResponseInterface

        setErrors(prev => ({
          ...prev,
          body: errors.fields?.currentEmail?.body?.join(''),
          fromEmail: errors.fields?.currentEmail?.fromEmail?.join(''),
          fromName: errors.fields?.currentEmail?.fromName?.join(''),
          previewText: errors.fields?.currentEmail?.previewText?.join(''),
          subject: errors.fields?.currentEmail?.subject?.join(''),
        }))

        if (e.errors.common) {
          const commonErrors = e.errors.common
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors,
            }
          })
        }
      } else {
        toast.error(t('global.error'))
      }
    } finally {
      setIsFetching(false)
    }
  }

  const isModalLoading = isDataLoading || isFetching

  return (
    <Modal
      isFetching={isModalLoading}
      className="flex flex-col gap-8"
      size={ModalSizeEnum.medium}
      title={t('dashboard.course.reminder.email_edit.title')}
      opened={opened}
      onClose={onClose}
    >
      <DashboardMailEditor
        withoutAttachments={true}
        isPreFetching={false}
        errors={{ ...errors }}
        tempState={tempState.currentEmail}
        onChangeEmail={data => setTempState({ currentEmail: data })}
        inModal
        additionalSubstitutes={[
          {
            slug: 'course_link',
            name: t('dashboard.course.reminder.email_edit.course_link'),
          },
        ]}
      />
      <div className="flex justify-end gap-3">
        <NeutralButton disabled={isModalLoading} onClick={onClose}>
          {t('global.cancel')}
        </NeutralButton>
        <PrimaryButton isFetching={isModalLoading} onClick={onSave}>
          <SaveIcon />
          {t('global.save')}
        </PrimaryButton>
      </div>
    </Modal>
  )
}

export default ReminderEmailModal
